<template>
  <section class="call-record-panel">
    <div class="call-record-toolbar">
      <span>共 {{ totalRecords }} 通</span>
      <button type="button" :disabled="loading" @click="loadRecords">{{ loading ? '刷新中...' : '刷新' }}</button>
    </div>
    <div v-if="loading && !records.length" class="call-record-state">正在加载通话记录...</div>
    <div v-else-if="errorMessage && !records.length" class="call-record-state call-record-error">
      <div>{{ errorMessage }}</div>
      <button class="call-record-retry" type="button" @click="loadRecords">重新加载</button>
    </div>
    <div v-else-if="!records.length" class="call-record-state">暂无通话</div>
    <template v-else>
      <div v-if="errorMessage" class="call-record-inline-error">{{ errorMessage }}</div>
      <div class="call-record-list">
        <article v-for="record in records" :key="record.id" class="call-record-card" :data-record-id="record.id">
          <div class="call-record-card-title">
            <span :class="['call-result-icon', { failed: !isAnsweredRecord(record) }]">{{ isAnsweredRecord(record) ? '●' : '×' }}</span>
            <strong>{{ record._targetName || '订单客户' }}</strong>
            <span>· {{ isAnsweredRecord(record) ? formatDuration(record.duration) : '呼叫失败' }}</span>
          </div>
          <div class="call-record-order">
            <span>关联单号：{{ record._orderNo || record.orderId || '--' }}</span>
            <button type="button" title="复制订单号" @click="copyOrderNo(record)">▣</button>
          </div>
          <div class="call-record-divider"></div>
          <div class="call-record-row">
            <span>呼出时间</span><span>{{ formatDateTime(record.startTime || record.createTime) }}</span>
          </div>
          <div class="call-record-row">
            <span>呼叫对象</span><span>{{ record._targetName || record.calleeMasked || '订单客户' }}</span>
          </div>
          <div v-if="isAnsweredRecord(record)" class="call-record-actions">
            <button type="button" :disabled="!record.recordStorageUrl" @click="openPlayback(record)">播放音频</button>
            <button class="primary" type="button" :disabled="!hasAiSummary(record)" @click="openAiSummary(record)">AI总结</button>
          </div>
          <div v-else class="call-record-failed-tip">本次未接通，无录音与AI总结</div>
        </article>
      </div>
    </template>

    <a-modal v-model="playbackVisible" title="语音播放与转写" width="900px" :maskClosable="false" @cancel="closePlayback">
      <div v-if="currentRecord" class="call-playback-dialog">
        <div class="call-playback-meta">
          <strong>{{ currentRecord._targetName || '订单客户' }}</strong>
          <span>{{ formatDateTime(currentRecord.startTime || currentRecord.createTime) }}</span>
          <span>通话时长：{{ formatDuration(currentRecord.duration) }}</span>
        </div>
        <audio
          v-if="audioUrl"
          ref="recordAudio"
          :key="audioUrl"
          :src="audioUrl"
          controls
          preload="metadata"
          class="call-record-audio"
          @timeupdate="handleAudioTimeUpdate"
          @loadedmetadata="handleAudioMetadataLoaded"
          @ended="activeSegmentIndex = -1"
          @error="handleAudioError"
        />
        <div v-else class="call-record-audio-empty">本次通话暂无可播放录音</div>
        <div v-if="audioError" class="call-record-audio-error">{{ audioError }}</div>
        <div class="call-transcript-heading">AI 语音转文字</div>
        <div v-if="transcriptSegments.length" class="call-transcript-list">
          <button
            v-for="(segment, index) in transcriptSegments"
            :key="index"
            type="button"
            :class="['call-transcript-line', segment.role, { active: activeSegmentIndex === index, disabled: !segment.timed || !audioUrl }]"
            :disabled="!segment.timed || !audioUrl"
            @click="seekToSegment(segment, index)"
          >
            <span class="call-transcript-speaker">{{ segment.speaker }}</span>
            <span class="call-transcript-time">{{ segment.timed ? formatMilliseconds(segment.begin) : '--:--' }}</span>
            <span class="call-transcript-text">{{ segment.text }}</span>
          </button>
        </div>
        <div v-else class="call-transcript-empty">{{ transcriptEmptyText(currentRecord) }}</div>
      </div>
      <template slot="footer">
        <a-button type="primary" :disabled="!currentRecord || !hasAiSummary(currentRecord)" @click="showAiSummaryFromPlayback">
          查看AI总结
        </a-button>
      </template>
    </a-modal>

    <a-modal v-model="aiSummaryVisible" title="AI通话总结" width="720px" :maskClosable="false">
      <div v-if="currentRecord" class="call-ai-dialog">
        <div class="call-ai-meta">
          <strong>{{ currentRecord._targetName || '订单客户' }}</strong>
          <span>{{ formatDateTime(currentRecord.startTime || currentRecord.createTime) }}</span>
          <span>关联单号：{{ currentRecord._orderNo || currentRecord.orderId || '--' }}</span>
        </div>
        <div class="call-ai-title">需求总结</div>
        <ul v-if="aiCustomerNeeds.length" class="call-ai-list">
          <li v-for="(item, index) in aiCustomerNeeds" :key="'need-' + index">{{ item }}</li>
        </ul>
        <div v-else class="call-ai-summary">{{ currentRecord.qaSummary || 'AI总结正在生成，请稍后刷新。' }}</div>
        <div class="call-ai-title">后续动作</div>
        <ul v-if="aiDesignerFollowUps.length" class="call-ai-list">
          <li v-for="(item, index) in aiDesignerFollowUps" :key="'follow-' + index">{{ item }}</li>
        </ul>
        <div v-else class="call-ai-summary">暂无需要跟进的后续动作</div>
      </div>
      <template slot="footer">
        <a-button :disabled="!audioUrl" @click="showPlaybackFromAiSummary">播放音频</a-button>
        <a-button type="primary" :disabled="!currentRecord || !hasAiSummary(currentRecord)" @click="copyAiSummary">
          复制AI总结
        </a-button>
      </template>
    </a-modal>
  </section>
</template>

<script>
import { message } from 'ant-design-vue'
import { requestHttpGet } from '@/api/main'
import apiUrl from '@/utils/config'

export default {
  name: 'CallRecordPanel',
  props: {
    orderOptions: { type: Array, default: () => [] },
    conversationId: { type: String, default: '' },
  },
  data() {
    return {
      loading: false,
      errorMessage: '',
      records: [],
      totalRecords: 0,
      selectedRecordId: null,
      playbackVisible: false,
      aiSummaryVisible: false,
      transcriptSegments: [],
      activeSegmentIndex: -1,
      audioError: '',
      pendingSeek: null,
      requestSequence: 0,
    }
  },
  computed: {
    currentRecord() {
      return this.records.find(item => Number(item.id) === Number(this.selectedRecordId)) || null
    },
    audioUrl() {
      return (this.currentRecord && this.currentRecord.recordStorageUrl) || ''
    },
    aiDetail() {
      return this.parseQaJson(this.currentRecord && this.currentRecord.qaResultJson)
    },
    aiCustomerNeeds() {
      return this.getAiItems(this.aiDetail, 'customerNeeds')
    },
    aiDesignerFollowUps() {
      const detail = this.aiDetail
      if (!detail) return []
      const source = Array.isArray(detail.designerFollowUps)
        ? detail.designerFollowUps
        : (Array.isArray(detail.followUps) ? detail.followUps : [])
      const managementOnly = /服务态度|质检(?:复核|结论)?|人工复核(?:录音|通话)|员工(?:培训|处罚)|培训员工|处罚员工|设备故障排查|工作环境排查|排查员工|合规审查|录音识别复核|转写质量复核/
      return source.filter(item => typeof item === 'string' && item.trim() && !managementOnly.test(item))
        .map(item => item.trim())
        .filter((item, index, values) => values.indexOf(item) === index)
    },
  },
  watch: {
    orderOptions: { deep: true, handler() { this.loadRecords() } },
  },
  mounted() {
    this.loadRecords()
  },
  beforeDestroy() {
    this.requestSequence += 1
    this.stopAudio()
    this.resetRecordState()
  },
  methods: {
    async loadRecords() {
      const conversationId = this.conversationId
      const options = Array.isArray(this.orderOptions) ? this.orderOptions.slice(0, 20) : []
      if (!conversationId || !options.length) {
        this.resetRecordState()
        return
      }
      const sequence = ++this.requestSequence
      this.loading = true
      this.errorMessage = ''
      try {
        const pages = await Promise.all(options.map(async order => {
          const result = await requestHttpGet(apiUrl + '/admin-api/supplier/call/im/record/page', {
            orderId: order.orderId, conversationId, pageNo: 1, pageSize: 20,
          })
          if (!result || result.code !== 0) throw new Error((result && result.msg) || '通话记录查询失败')
          const page = result.data || {}
          return {
            total: Number(page.total) || 0,
            list: (Array.isArray(page.list) ? page.list : []).map(record => ({
              ...record,
              _orderNo: order.orderNo || order.orderId,
              _targetName: order.customerName || '订单客户',
              _targetAvatar: order.customerAvatar || '',
            })),
          }
        }))
        if (sequence !== this.requestSequence) return
        this.totalRecords = pages.reduce((sum, page) => sum + page.total, 0)
        this.records = pages.reduce((all, page) => all.concat(page.list), [])
          .sort((left, right) => this.recordTime(right) - this.recordTime(left))
        if (!this.records.some(item => Number(item.id) === Number(this.selectedRecordId))) {
          this.selectedRecordId = null
          this.closePlayback()
          this.aiSummaryVisible = false
        }
      } catch (error) {
        if (sequence !== this.requestSequence) return
        this.errorMessage = this.getErrorMessage(error)
      } finally {
        if (sequence === this.requestSequence) this.loading = false
      }
    },
    recordTime(record) {
      const value = record && (record.startTime || record.createTime)
      const timestamp = this.toTimestamp(value)
      return Number.isFinite(timestamp) ? timestamp : 0
    },
    isAnsweredRecord(record) {
      return Boolean(record && (record.answerTime || Number(record.duration) > 0))
    },
    hasAiSummary(record) {
      return Boolean(record && record.qaStatus === 'SUCCEEDED' && (record.qaSummary || record.qaResultJson))
    },
    openPlayback(record) {
      if (!record || !record.recordStorageUrl) {
        message.warning('本次通话录音尚未生成')
        return
      }
      this.selectedRecordId = record.id
      this.transcriptSegments = this.parseTranscript(record.transcript)
      this.activeSegmentIndex = -1
      this.audioError = ''
      this.pendingSeek = null
      this.playbackVisible = true
    },
    closePlayback() {
      this.stopAudio()
      this.playbackVisible = false
      this.transcriptSegments = []
      this.activeSegmentIndex = -1
      this.audioError = ''
      this.pendingSeek = null
    },
    openAiSummary(record) {
      if (!this.hasAiSummary(record)) {
        message.warning('AI总结尚未生成')
        return
      }
      this.selectedRecordId = record.id
      this.aiSummaryVisible = true
    },
    showAiSummaryFromPlayback() {
      const record = this.currentRecord
      if (!record) return
      this.closePlayback()
      this.$nextTick(() => this.openAiSummary(record))
    },
    showPlaybackFromAiSummary() {
      const record = this.currentRecord
      if (!record) return
      this.aiSummaryVisible = false
      this.$nextTick(() => this.openPlayback(record))
    },
    copyAiSummary() {
      const value = this.buildAiSummaryText()
      if (!value) {
        message.warning('AI总结尚未生成')
        return
      }
      this.copyText(value)
      message.success('AI总结已复制')
    },
    parseQaJson(value) {
      if (!value) return null
      try {
        const parsed = JSON.parse(value)
        return parsed && typeof parsed === 'object' ? parsed : null
      } catch (error) {
        return null
      }
    },
    getAiItems(detail, field) {
      const items = detail && detail[field]
      return Array.isArray(items)
        ? items.filter(item => typeof item === 'string' && item.trim()).map(item => item.trim())
          .filter((item, index, values) => values.indexOf(item) === index)
        : []
    },
    buildAiSummaryText() {
      if (!this.currentRecord) return ''
      const sections = []
      const needs = this.aiCustomerNeeds.length
        ? this.aiCustomerNeeds
        : [this.currentRecord.qaSummary].filter(Boolean)
      if (needs.length) sections.push(`需求总结：\n${needs.map(item => `• ${item}`).join('\n')}`)
      if (this.aiDesignerFollowUps.length) {
        sections.push(`后续动作：\n${this.aiDesignerFollowUps.map(item => `• ${item}`).join('\n')}`)
      }
      return sections.join('\n\n')
    },
    copyOrderNo(record) {
      const value = String((record && (record._orderNo || record.orderId)) || '')
      if (!value) return
      this.copyText(value)
      message.success('订单号已复制')
    },
    copyText(value) {
      const input = document.createElement('textarea')
      input.value = value
      input.style.position = 'fixed'
      input.style.opacity = '0'
      document.body.appendChild(input)
      input.select()
      document.execCommand('copy')
      document.body.removeChild(input)
    },
    parseTranscript(value) {
      if (!value) return []
      return String(value).split(/\r?\n/).map(line => {
        const timed = line.match(/^\[(\d+)-(\d+)]\s*(.+)$/)
        const content = (timed ? timed[3] : line).trim()
        const speaker = content.match(/^(客户\(A\)|员工\(B\)|客户|员工|说话人A|说话人B)[：:]\s*/)
        const speakerName = speaker ? speaker[1].replace('(A)', '').replace('(B)', '') : '转写'
        return {
          begin: timed ? Number(timed[1]) : 0,
          end: timed ? Number(timed[2]) : 0,
          timed: Boolean(timed),
          speaker: speakerName,
          role: /客户|说话人A/.test(speakerName) ? 'customer' : 'employee',
          text: speaker ? content.slice(speaker[0].length).trim() : content,
        }
      }).filter(item => item.text)
    },
    seekToSegment(segment, index) {
      if (!segment.timed || !this.audioUrl) return
      const audio = this.$refs.recordAudio
      if (!audio) return
      this.audioError = ''
      const target = { seconds: Math.max(0, segment.begin / 1000), index }
      if (audio.readyState === 0) {
        this.pendingSeek = target
        this.activeSegmentIndex = index
        audio.load()
        return
      }
      this.playAt(target)
    },
    handleAudioMetadataLoaded() {
      if (!this.pendingSeek) return
      const target = this.pendingSeek
      this.pendingSeek = null
      this.playAt(target)
    },
    playAt(target) {
      const audio = this.$refs.recordAudio
      if (!audio || !target) return
      try {
        audio.currentTime = target.seconds
        const playResult = audio.play()
        if (playResult && typeof playResult.catch === 'function') {
          playResult.catch(() => { this.audioError = '录音暂时无法播放，请检查网络后重试。' })
        }
        this.activeSegmentIndex = target.index
      } catch (error) {
        this.audioError = '录音尚未加载完成，请稍后再试。'
      }
    },
    handleAudioTimeUpdate(event) {
      const milliseconds = event.target.currentTime * 1000
      const found = this.transcriptSegments.findIndex((item, index) => {
        if (!item.timed || milliseconds < item.begin) return false
        const next = this.transcriptSegments.slice(index + 1).find(candidate => candidate.timed)
        const boundary = item.end > item.begin ? item.end : (next ? next.begin : Number.MAX_SAFE_INTEGER)
        return milliseconds < boundary
      })
      this.activeSegmentIndex = found
    },
    handleAudioError() {
      this.audioError = '录音加载失败，请稍后刷新；若持续失败请联系技术人员检查录音文件地址。'
      this.activeSegmentIndex = -1
    },
    stopAudio() {
      const audio = this.$refs.recordAudio
      if (audio && typeof audio.pause === 'function') audio.pause()
    },
    resetRecordState() {
      this.records = []
      this.totalRecords = 0
      this.errorMessage = ''
      this.loading = false
      this.selectedRecordId = null
      this.closePlayback()
      this.aiSummaryVisible = false
    },
    formatDateTime(value) {
      if (!value) return '时间待更新'
      const timestamp = this.toTimestamp(value)
      if (!Number.isFinite(timestamp)) return String(value).replace('T', ' ').slice(0, 19)
      const date = new Date(timestamp)
      const pad = number => String(number).padStart(2, '0')
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
    },
    toTimestamp(value) {
      if (value === null || value === undefined || value === '') return NaN
      const numeric = Number(value)
      if (Number.isFinite(numeric)) {
        // 后端日期在部分 PCIM 环境中会序列化为毫秒时间戳；同时兼容秒级时间戳。
        return numeric > 0 && numeric < 100000000000 ? numeric * 1000 : numeric
      }
      return new Date(String(value).replace(' ', 'T')).getTime()
    },
    formatDuration(value) {
      const seconds = Number(value)
      if (!Number.isFinite(seconds) || seconds < 0) return '--:--'
      const hours = Math.floor(seconds / 3600)
      const minutes = Math.floor((seconds % 3600) / 60)
      const secs = Math.floor(seconds % 60)
      return hours > 0
        ? `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`
        : `${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`
    },
    formatMilliseconds(value) {
      return this.formatDuration(Math.floor((Number(value) || 0) / 1000))
    },
    transcriptEmptyText(record) {
      if (record && (record.qaStatus === 'PENDING' || record.qaStatus === 'PROCESSING')) return '语音正在转写，请稍后刷新。'
      if (record && record.qaStatus === 'FAILED') return '本次转写失败，请联系技术人员检查处理记录。'
      return '暂无转写内容。'
    },
    getErrorMessage(error) {
      const status = error && error.response && error.response.status
      if (status === 404) return '通话记录服务未更新（HTTP 404），请确认已部署最新供应商后端。'
      if (status === 403) return '当前账号没有查看本项目通话记录的权限。'
      if (status === 502 || status === 503 || status === 504) return `通话记录服务暂不可用（HTTP ${status}），请稍后重试。`
      if (error && (error.code === 'ECONNABORTED' || /timeout/i.test(error.message || ''))) return '通话记录加载超时，请检查网络后重试。'
      if (error && error.response && error.response.data && error.response.data.msg) return error.response.data.msg
      return (error && error.message) || '通话记录加载失败，请稍后重试。'
    },
  },
}
</script>

<style lang="less" scoped>
.call-record-panel { color: #222; }
.call-record-toolbar { display: flex; align-items: center; justify-content: space-between; margin-bottom: 8px; color: #999; font-size: 12px; }
.call-record-toolbar button, .call-record-retry { padding: 3px 9px; border: 1px solid #ddd; border-radius: 4px; color: #666; background: #fff; cursor: pointer; }
.call-record-toolbar button:disabled { color: #bbb; cursor: default; }
.call-record-state { padding: 32px 8px; border-radius: 6px; color: #999; background: #fafafa; text-align: center; }
.call-record-error, .call-record-inline-error, .call-record-audio-error { color: #d93026; }
.call-record-retry { margin-top: 12px; }
.call-record-inline-error { margin-bottom: 8px; padding: 8px; border-radius: 5px; background: #fff2f0; font-size: 12px; }
.call-record-list { display: flex; flex-direction: column; gap: 10px; }
.call-record-card { padding: 12px; border: 1px solid #eee; border-radius: 10px; background: #fff; box-shadow: 0 2px 10px rgba(0, 0, 0, .04); }
.call-record-card-title { display: flex; align-items: center; overflow: hidden; color: #333; font-size: 13px; white-space: nowrap; }
.call-record-card-title strong { max-width: 92px; overflow: hidden; text-overflow: ellipsis; }
.call-result-icon { margin-right: 5px; color: #1ab769; font-size: 15px; }
.call-result-icon.failed { color: #f04444; }
.call-record-order { display: flex; align-items: center; justify-content: space-between; margin-top: 7px; color: #888; font-size: 11px; }
.call-record-order span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.call-record-order button { flex: 0 0 auto; padding: 0 2px; border: 0; color: #999; background: transparent; cursor: pointer; }
.call-record-divider { height: 1px; margin: 9px 0; background: #eee; }
.call-record-row { display: flex; justify-content: space-between; gap: 8px; margin: 6px 0; color: #999; font-size: 11px; }
.call-record-row span:last-child { overflow: hidden; color: #666; text-align: right; text-overflow: ellipsis; white-space: nowrap; }
.call-record-actions { display: flex; justify-content: flex-end; gap: 7px; margin-top: 10px; }
.call-record-actions button { height: 27px; padding: 0 10px; border: 1px solid #ddd; border-radius: 4px; color: #555; background: #fff; font-size: 11px; cursor: pointer; }
.call-record-actions button.primary { border-color: #ed2616; color: #fff; background: #ed2616; }
.call-record-actions button:disabled { border-color: #eee; color: #bbb; background: #f7f7f7; cursor: not-allowed; }
.call-record-failed-tip { margin-top: 8px; color: #aaa; font-size: 11px; text-align: right; }
.call-playback-meta, .call-ai-meta { display: flex; gap: 16px; margin-bottom: 14px; color: #888; font-size: 12px; }
.call-playback-meta strong, .call-ai-meta strong { color: #333; font-size: 14px; }
.call-record-audio { width: 100%; height: 40px; }
.call-record-audio-empty { padding: 14px; border-radius: 6px; color: #999; background: #f7f7f7; text-align: center; }
.call-record-audio-error { margin-top: 7px; font-size: 12px; }
.call-transcript-heading, .call-ai-title { margin: 18px 0 9px; font-size: 14px; font-weight: 600; }
.call-transcript-list { min-height: 360px; max-height: calc(100vh - 360px); padding: 10px; overflow-y: auto; border: 1px solid #eee; border-radius: 7px; background: #f7f7f7; }
.call-transcript-line { display: grid; grid-template-columns: 58px 48px 1fr; width: 100%; margin-bottom: 8px; padding: 10px; border: 1px solid transparent; border-radius: 6px; color: #333; background: #fff; text-align: left; cursor: pointer; }
.call-transcript-line:last-child { margin-bottom: 0; }
.call-transcript-line:hover, .call-transcript-line.active { border-color: #ffb5a8; background: #fff1ee; }
.call-transcript-line.disabled { cursor: default; }
.call-transcript-line.disabled:hover { border-color: transparent; background: #fff; }
.call-transcript-speaker { color: #1677ff; font-weight: 600; }
.call-transcript-line.employee .call-transcript-speaker { color: #14a05a; }
.call-transcript-time { color: #999; }
.call-transcript-text { line-height: 1.55; }
.call-transcript-empty, .call-ai-summary { padding: 18px; border-radius: 7px; color: #555; background: #f7f7f7; line-height: 1.8; white-space: pre-wrap; }
.call-ai-list { margin: 0; padding: 14px 18px 14px 38px; border-radius: 7px; color: #444; background: #f7f7f7; line-height: 1.8; }
.call-ai-list li + li { margin-top: 5px; }
</style>
